import * as React from "react"
import { Box, useTheme, useMediaQuery } from "@mui/material"
import Head from "next/head"
import { useRouter } from "next/router"
import { ErrorBoundary } from "react-error-boundary"
import toast from "react-hot-toast"
import * as Sentry from "@sentry/nextjs"
import { datadogRum } from "@datadog/browser-rum"

import ClientOnly from "components/ClientOnly"
import ImpersonationBanner from "components/ImpersonationBanner"
import PrimarySidebar from "components/sidebars/PrimarySidebar"
import PrimaryMobileNav from "components/sidebars/PrimaryMobileNav"
import SecondaryMobileDrawer from "components/sidebars/SecondaryMobileDrawer"
import PageLoader from "components/PageLoader"
import DashboardFooter from "components/DashboardFooter"
import HelpMenu from "components/HelpMenu"
import EmptyMessage from "components/EmptyMessage"
import { hasPlanFeatures } from "components/PlanGate"
import { useAuth } from "context/auth"
import {
  useCurrentUser,
  useLogUserSession,
  useUpdateConfirmUser,
} from "context/api"
import { useSiteSettings } from "context/siteSettings"
import { configGtag } from "util/google-analytics"
import { configFullstory } from "util/fullstory"
import { getRawDate, getPastDate } from "util/formatters"
import {
  sidebarDefs,
  PRIMARY_SIDEBAR_WIDTH,
  SM_PRIMARY_SIDEBAR_WIDTH,
  SECONDARY_SIDEBAR_WIDTH,
} from "data/sidebars"
import useImpersonateOnLoad from "hooks/useImpersonateOnLoad"

export default function DashboardLayout({ children }) {
  const theme = useTheme()
  const lessThanMdBreakpoint = useMediaQuery(theme.breakpoints.down("md"))
  const shouldCollapsePrimarySidebar = lessThanMdBreakpoint
  const { isAuthenticated, isImpersonating, user } = useAuth()
  // these are only used to identify a Luz user when impersonating
  const luzUserId = user?.sub
  const luzUserEmail = user?.email
  const { data: currentUser } = useCurrentUser()
  const userEmail = currentUser?.email || ""
  const userId = currentUser?.id || ""
  const userConfirmed = currentUser?.confirmed || false
  const userGroupId = currentUser?.group?.id || ""
  const { mutate: logUserSession } = useLogUserSession()
  const { mutate: updateUser } = useUpdateConfirmUser()
  const isProduction = process.env.NODE_ENV === "production"
  const isLuzUser =
    !!userEmail &&
    (userEmail?.includes("luzdata") ||
      userEmail?.includes("tryluz") ||
      userEmail?.includes("discoverluz") ||
      isImpersonating === true)
  useImpersonateOnLoad()

  // values to determine if dates can be set from the url
  const router = useRouter()
  const isOnStaticPage = router?.pathname?.startsWith(
    "/dashboard/companies/[id]/static"
  )
  const { setSelectedStartDate, setSelectedEndDate } = useSiteSettings()
  const startDateFromUrl = router?.query?.sd
  const endDateFromUrl = router?.query?.ed
  const hasHistoricalDataPermissions = hasPlanFeatures({
    user: currentUser,
    allowedFeatures: ["historicalData"],
  })
  const historicalStartDateMin = React.useMemo(
    () => getRawDate(getPastDate(31, "day")),
    []
  )
  // set start and end dates based on the URL provided
  React.useEffect(() => {
    if (!startDateFromUrl || !endDateFromUrl) {
      return
    }

    const rawStartDate = getRawDate(startDateFromUrl)
    const rawEndDate = getRawDate(endDateFromUrl)
    if (rawStartDate.valueOf() >= rawEndDate.valueOf()) {
      console.warn("provided dates from URL are invalid")
    } else if (!rawStartDate.isValid()) {
      console.warn("the provided start dates from URL is invalid")
    } else if (!rawEndDate.isValid()) {
      console.warn("the provided start dates from URL is invalid")
    } else if (
      !hasHistoricalDataPermissions &&
      rawStartDate.valueOf() < historicalStartDateMin.valueOf()
    ) {
      console.warn(
        "the start date is too early, historical data is not allowed for this user"
      )
    } else {
      setSelectedStartDate(rawStartDate.format("YYYY-MM-DD"))
      setSelectedEndDate(rawEndDate.format("YYYY-MM-DD"))
    }
  }, [
    startDateFromUrl,
    endDateFromUrl,
    setSelectedStartDate,
    setSelectedEndDate,
    hasHistoricalDataPermissions,
    historicalStartDateMin,
  ])

  // allow an app wide error message based on an env var
  React.useEffect(() => {
    if (process.env.NEXT_PUBLIC_ERROR_MESSAGE) {
      toast.error(process.env.NEXT_PUBLIC_ERROR_MESSAGE, { duration: 10000 })
    }
  }, [])

  // update a users confirmation status in dynamo
  React.useEffect(() => {
    if (!!userEmail && isAuthenticated && !userConfirmed) {
      updateUser({ confirmed: true })
    }
  }, [isAuthenticated, userConfirmed, updateUser, userEmail])

  // track the initial pageview once the user is loaded
  React.useEffect(() => {
    if (isAuthenticated) {
      configGtag({
        options: {
          page_path: window?.location?.pathname,
          custom_map: {
            dimension1: "group_id",
          },
          group_id: userGroupId,
          user_id: userId,
        },
      })
    }
  }, [isAuthenticated, userId, userEmail, userGroupId])

  // init DataDog Real User Metrics
  React.useEffect(() => {
    if (isProduction && isAuthenticated && !!userEmail) {
      datadogRum.init({
        applicationId: "0e0693be-95c9-4442-be17-43f19d99b32e",
        clientToken: "pub99cc53bc3cbc934c8a603de2f5cf2950",
        site: "datadoghq.com",
        service: "luz-web",
        // Specify a version number to identify the deployed version of your application in Datadog
        version: process.env.NEXT_PUBLIC_GIT_SHA || "1.0.0",
        sampleRate: 100,
        premiumSampleRate: 100,
        trackInteractions: true,
        trackViewsManually: true,
        defaultPrivacyLevel: "mask-user-input",
      })

      if (!isLuzUser) {
        // only record non Luz premium sessions
        datadogRum.startSessionReplayRecording()
      }
    } else {
      if (isImpersonating) {
        // when impersonating, don't record the session
        datadogRum.stopSessionReplayRecording()
      }
    }
  }, [
    isAuthenticated,
    isImpersonating,
    userEmail,
    userId,
    userGroupId,
    isProduction,
    isLuzUser,
  ])

  // set user for Datadog
  React.useEffect(() => {
    if (isProduction && isAuthenticated && !!userEmail) {
      if (isImpersonating) {
        datadogRum.setUser({
          id: luzUserId,
          email: luzUserEmail,
          groupId: "00000",
          impersonationId: userId,
          impersonationEmail: userEmail,
        })
      } else {
        datadogRum.setUser({
          id: userId,
          email: userEmail,
          groupId: userGroupId,
        })
      }
    }
  }, [
    isAuthenticated,
    isImpersonating,
    isProduction,
    userEmail,
    userId,
    userGroupId,
    luzUserEmail,
    luzUserId,
  ])

  // set user for Custify
  React.useEffect(() => {
    if (isProduction && isAuthenticated && !!userEmail) {
      if (isImpersonating) {
        window._ctrack.identify({
          userId: luzUserId,
          email: luzUserEmail,
          company_id: "00000",
        })
      } else {
        window._ctrack.identify({
          userId: userId,
          email: userEmail,
          company_id: userGroupId,
        })
      }
    }
  }, [
    isAuthenticated,
    isImpersonating,
    isProduction,
    userEmail,
    userId,
    userGroupId,
    luzUserEmail,
    luzUserId,
  ])

  // configure Sentry
  React.useEffect(() => {
    if (isAuthenticated && Sentry) {
      Sentry.setUser({
        email: userEmail,
        groupId: userGroupId,
      })
    }
  }, [isAuthenticated, userEmail, userGroupId])

  // send event to signify start of user session for our own tracking
  React.useEffect(() => {
    if (isAuthenticated) {
      if (!isLuzUser && !isImpersonating && !!userEmail) {
        logUserSession()
      }
    }
    // we're purposefully not adding logUserSession to the dependency list to avoid infinite loops
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, isImpersonating, isLuzUser, userEmail])

  // connect user to current user in fullstory
  React.useEffect(() => {
    if (!isLuzUser && !!userId && !isImpersonating) {
      configFullstory({ userId: userId, email: userEmail })
    }
  }, [userEmail, userId, isImpersonating, isLuzUser])

  // track subsequent pageviews as history of browser changes
  React.useEffect(() => {
    const handleRouteChange = async url => {
      // give 200 ms (maybe too short?) for page title to update
      await new Promise(r => setTimeout(r, 200))
      configGtag({
        options: {
          page_path: url,
          custom_map: {
            dimension1: "group_id",
          },
          group_id: currentUser?.group?.id,
          user_id: currentUser?.id,
        },
      })
      datadogRum.startView(url)
    }
    router.events.on("routeChangeComplete", handleRouteChange)
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange)
    }
  }, [router.events, currentUser])

  // render an empty shell for unauthed users, and when auth passes let them through
  let dashboardChildren = <PageLoader />
  if (currentUser?.group?.status === "disabled") {
    dashboardChildren = <DisabledDashboard />
  } else if (isAuthenticated === undefined) {
    dashboardChildren = <PageLoader />
  } else if (isAuthenticated === false) {
    const redirectTo = window?.location?.pathname
    router.push(`/auth/signin?redirectTo=${redirectTo}`)
    dashboardChildren = <PageLoader />
  } else if (isAuthenticated === true) {
    dashboardChildren = children
  } else {
    dashboardChildren = <PageLoader />
  }

  // decide whether to use solely a primary sidebar or a primary and secondary sidebar
  let layoutTemplateColumns = {
    xs: "1fr",
    sm: `64px 1fr`,
    md: `${PRIMARY_SIDEBAR_WIDTH}px 1fr`,
  }
  let dashboardContentMaxWidth = {
    xs: "100vw",
    sm: `calc(100vw - ${SM_PRIMARY_SIDEBAR_WIDTH}px)`,
    md: `calc(100vw - ${PRIMARY_SIDEBAR_WIDTH}px)`,
  }
  let secondarySidebar = null
  sidebarDefs.forEach(sidebarDef => {
    if (router?.pathname && router?.pathname?.startsWith(sidebarDef.basePath)) {
      layoutTemplateColumns = {
        xs: "1fr",
        sm: `64px ${SECONDARY_SIDEBAR_WIDTH}px 1fr`,
        md: `${PRIMARY_SIDEBAR_WIDTH}px ${SECONDARY_SIDEBAR_WIDTH}px 1fr`,
      }
      dashboardContentMaxWidth = {
        xs: "100vw",
        sm: `calc(100vw - ${SM_PRIMARY_SIDEBAR_WIDTH}px - ${SECONDARY_SIDEBAR_WIDTH}px)`,
        md: `calc(100vw - ${PRIMARY_SIDEBAR_WIDTH}px - ${SECONDARY_SIDEBAR_WIDTH}px)`,
      }
      secondarySidebar = sidebarDef.secondarySidebar
    }
  })

  return (
    <>
      <Head>
        <title key="title">Dashboard | Luz</title>
      </Head>
      <ClientOnly
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 1,
          displayPrint: "none",
        }}
      >
        <ImpersonationBanner />
      </ClientOnly>
      <Box
        minHeight="100vh"
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: layoutTemplateColumns,
            width: "100%",
            height: "100%",
            flex: 1,
          }}
        >
          <Box
            sx={{
              display: { xs: "none", sm: "inherit" },
            }}
          >
            <PrimarySidebar
              collapse={shouldCollapsePrimarySidebar}
              isStaticPage={isOnStaticPage}
            />
          </Box>
          {secondarySidebar && (
            <Box
              sx={{
                display: { xs: "none", sm: "inherit" },
              }}
            >
              {secondarySidebar}
            </Box>
          )}
          <PrimaryMobileNav />
          <SecondaryMobileDrawer>{secondarySidebar}</SecondaryMobileDrawer>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              width: "100%",
              flex: 1,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                flex: 1,
                height: "100%",
                width: "100%",
                maxWidth: dashboardContentMaxWidth,
              }}
            >
              <ErrorBoundary
                FallbackComponent={
                  <Box
                    sx={{
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <EmptyMessage
                      type="error"
                      message="Oops, something went wrong loading the dashboard"
                    />
                  </Box>
                }
              >
                {dashboardChildren}
              </ErrorBoundary>
              {isOnStaticPage ? null : <DashboardFooter />}
            </Box>
          </Box>
        </Box>
        <HelpMenu />
        <Box
          id="leftConfetti"
          component="span"
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
          }}
        ></Box>
        <Box
          id="rightConfetti"
          component="span"
          sx={{
            position: "absolute",
            bottom: 0,
            right: 0,
          }}
        ></Box>
      </Box>
    </>
  )
}

function DisabledDashboard() {
  return (
    <Box sx={{ pt: 8 }}>
      <EmptyMessage message="Your account has been disabled. Contact Luz support for more information." />
    </Box>
  )
}
